import { getFragmentData } from 'gql'
import { ErrorFragmentFragmentDoc } from 'gql/graphql'
import { isNil } from 'lodash'
import { SnackbarKey, useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { updateUser } from 'features/user'
import { useAbility } from 'providers/casl'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { fromDTO, toDTO } from './mapper'
import { UPDATE_CURRENT_WORKSPACE } from './mutation'

import type { Props, UpdateStatus, UseUpdateCurrentWorkspace, Variables } from './types'
import type {
  UpdateCurrentWorkspaceMutation,
  UpdateCurrentWorkspaceMutationVariables,
} from 'gql/graphql'
import type { GraphQlResponse } from 'providers/tanstack-query/response'

export const useUpdateCurrentWorkspace = ({ userId }: Props): UseUpdateCurrentWorkspace => {
  const { t } = useTranslation('features/workspace')
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const ability = useAbility()

  const enqueueError = (): SnackbarKey =>
    enqueueSnackbar(t('updateCurrentWorkspace.error'), { variant: 'error' })

  const { mutateAsync } = useMutation<
    UpdateCurrentWorkspaceMutationVariables,
    UpdateCurrentWorkspaceMutation
  >({
    mutation: UPDATE_CURRENT_WORKSPACE,
    onCompleted: async ({ updateCurrentWorkspace }: UpdateCurrentWorkspaceMutation) => {
      const errors = getFragmentData(ErrorFragmentFragmentDoc, updateCurrentWorkspace?.errors)
      const user = updateCurrentWorkspace?.user

      if (errors && errors.length > 0) {
        enqueueError()
      } else if (user) {
        const { currentWorkspace, currentWorkspaceRole, workspaceProducts } = fromDTO(user)

        dispatch(updateUser({ currentWorkspace, currentWorkspaceRole, workspaceProducts }))
      }
    },
    queriesToInvalidate: ['getWorkspaceProducts'],
  })

  const update = async (id?: Variables['currentWorkspaceId']): Promise<UpdateStatus> => {
    const result: GraphQlResponse<UpdateCurrentWorkspaceMutation> | void = await mutateAsync({
      canAccessGamConnection: ability.can('access', 'GlobalSetupPage-googleAdManagerConnection'),
      input: toDTO({
        currentWorkspaceId: id,
        id: userId,
      }),
    })

    if (result === undefined) {
      return 'failure'
    }

    const responseErrorsLength = result.errors?.length
    const mutationErrorsLength = result.data.updateCurrentWorkspace?.errors?.length

    if (isNil(responseErrorsLength) && isNil(mutationErrorsLength)) {
      return 'success'
    } else {
      return 'failure'
    }
  }

  return { update }
}
